import { ReservedResource } from '@logto/core-kit';
import { type ConsentInfoResponse } from '@logto/schemas';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Link, TextBlock, Button } from '@adviser-group/aberdeen-design-system';

import LandingPageLayout from '@/Layout/LandingPageLayout';
import { consent, getConsentInfo } from '@/apis/consent';
import useApi from '@/hooks/use-api';
import useErrorHandler, { type ErrorHandlers } from '@/hooks/use-error-handler';
import useGlobalRedirectTo from '@/hooks/use-global-redirect-to';
import PageContext from '@/Providers/PageContextProvider/PageContext';
import ErrorPage from '@/pages/ErrorPage';
import { searchKeys } from '@/shared/utils/search-parameters';

import FirmSelector from './FirmSelector';
import OrganizationSelector, { type Organization } from './OrganizationSelector';
import ScopesListCard from './ScopesListCard';
import styles from './index.module.scss';

export type FirmOption = {
  id: string;
  name: string;
  siteDefaultUrl: string;
};

const Consent = () => {
  const handleError = useErrorHandler();
  const asyncConsent = useApi(consent);
  const redirectTo = useGlobalRedirectTo();

  const { setLoading } = useContext(PageContext);

  const [consentData, setConsentData] = useState<ConsentInfoResponse>();
  const [selectedOrganization, setSelectedOrganization] = useState<Organization>();
  const [selectedFirm, setSelectedFirm] = useState<string>('');
  const [firmOptions, setFirmOptions] = useState<FirmOption[]>([]);
  const [firmError, setFirmError] = useState<string>('');
  const [isAccessDenied, setIsAccessDenied] = useState(false);

  const [isConsentLoading, setIsConsentLoading] = useState(false);

  const asyncGetConsentInfo = useApi(getConsentInfo);

  const consentErrorHandlers: ErrorHandlers = useMemo(
    () => ({
      'oidc.access_denied': () => {
        setIsAccessDenied(true);
      },
    }),
    []
  );

  const handleConsentError = useCallback(
    async (error: unknown) => {
      await handleError(error, consentErrorHandlers);
    },
    [consentErrorHandlers, handleError]
  );

  const signOut = useCallback(() => {
    const applicationId =
      new URLSearchParams(window.location.search).get(searchKeys.appId) ??
      consentData?.application.id;
    const signOutUrl = new URL('/oidc/session/end', window.location.origin);

    if (applicationId) {
      signOutUrl.searchParams.set('client_id', applicationId);
    }

    window.location.assign(signOutUrl.href);
  }, [consentData?.application.id]);

  const consentHandler = useCallback(async () => {
    if (!selectedFirm) {
      setFirmError('Please select a firm to continue.');
      return;
    }

    const firmOption = firmOptions.find((option) => option.id === selectedFirm);
    if (!firmOption) {
      setFirmError('Invalid firm selection.');
      return;
    }

    setIsConsentLoading(true);
    setFirmError('');

    try {
      // First save the firm selection
      const selectFirmResponse = await fetch('/api/interaction/consent/select-firm', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firmId: firmOption.id,
          firmName: firmOption.name,
          siteDefaultUrl: firmOption.siteDefaultUrl,
        }),
      });

      if (!selectFirmResponse.ok) {
        throw new Error('Failed to save firm selection');
      }

      // Then proceed with normal consent
      const [error, result] = await asyncConsent(selectedOrganization?.id);

      if (error) {
        await handleConsentError(error);
        return;
      }

      if (result?.redirectTo) {
        await redirectTo(result.redirectTo);
      }
    } catch (error) {
      console.error('Failed to process consent:', error);
      setFirmError('Failed to process your selection. Please try again.');
    } finally {
      setIsConsentLoading(false);
    }
  }, [
    asyncConsent,
    handleConsentError,
    redirectTo,
    selectedOrganization?.id,
    selectedFirm,
    firmOptions,
  ]);

  useEffect(() => {
    const initConsentPage = async () => {
      setLoading(true);
      setIsConsentLoading(true);
      try {
        // Load consent info and firm options in parallel
        const [consentError, consentResult] = await asyncGetConsentInfo();

        if (consentError) {
          await handleConsentError(consentError);
          return;
        }

        setConsentData(consentResult);

        // Init the default organization selection
        if (consentResult?.organizations?.length) {
          setSelectedOrganization(consentResult.organizations[0]);
        }

        // Load firm options
        const firmResponse = await fetch('/api/interaction/consent/firm-options');
        if (firmResponse.ok) {
          const options: FirmOption[] = await firmResponse.json();
          setFirmOptions(options);
        } else {
          setFirmError('Failed to load firm options. Please try again.');
        }
      } catch (error) {
        console.error('Failed to initialize consent page:', error);
        setFirmError('Failed to load page. Please try again.');
      } finally {
        setIsConsentLoading(false);
        setLoading(false);
      }
    };

    void initConsentPage();
  }, [asyncGetConsentInfo, handleConsentError, setLoading]);

  if (isAccessDenied) {
    return (
      <ErrorPage
        isNavbarHidden
        title="error.access_denied"
        message="error.application_access_denied"
        primaryAction={{
          title: 'account_center.sessions.revoke_session',
          onClick: signOut,
        }}
      />
    );
  }

  if (!consentData) {
    if (isConsentLoading) {
      return (
        <LandingPageLayout title="description.authorize_title">
          <TextBlock>Loading...</TextBlock>
        </LandingPageLayout>
      );
    }

    return (
      <LandingPageLayout title="description.authorize_title">
        <TextBlock>
          {firmError || 'Failed to load consent information. Please try again.'}
        </TextBlock>
      </LandingPageLayout>
    );
  }

  const {
    application: { displayName, name },
  } = consentData;

  const applicationName = displayName ?? name;

  return (
    <LandingPageLayout
      title="description.authorize_title"
      titleInterpolation={{
        name: applicationName,
      }}
      thirdPartyBranding={consentData.application.branding}
      applicationName={applicationName}
    >
      {/* Firm Selection Section */}
      <FirmSelector
        firmOptions={firmOptions}
        selectedFirm={selectedFirm}
        error={firmError}
        className={styles.firmSelector}
        onFirmSelect={setSelectedFirm}
        onErrorClear={() => {
          setFirmError('');
        }}
      />

      <ScopesListCard
        userScopes={consentData.missingOIDCScope}
        /**
         * The org resources is included in the user scopes for compatibility.
         */
        resourceScopes={consentData.missingResourceScopes?.filter(
          ({ resource }) => resource.id !== ReservedResource.Organization
        )}
        appName={applicationName}
        className={styles.scopesCard}
      />
      {consentData.organizations && (
        <OrganizationSelector
          className={styles.organizationSelector}
          organizations={consentData.organizations}
          selectedOrganization={selectedOrganization}
          onSelect={setSelectedOrganization}
        />
      )}
      <div className={styles.textTop}>
        <TextBlock color="secondary" size="static-extra-small" variant="body" alignment="center">
          All data will be processed in line with our{' '}
          <Link size="small" href="https://www.aberdeenadviser.com/en-gb/privacy">
            Privacy policy
          </Link>
          . Please note: You will need to re-authorise this connection every 30 days.
        </TextBlock>
      </div>
      <div className={styles.footerButton}>
        <Button
          title="action.authorize"
          priority="primary"
          loading={isConsentLoading}
          disabled={!selectedFirm || isConsentLoading}
          onPress={consentHandler}
        >
          Authorise
        </Button>
        <Button
          title="action.cancel"
          priority="secondary"
          type="button"
          onPress={() => {
            if (consentData.redirectUri) {
              window.location.replace(consentData.redirectUri);
            }
          }}
        >
          Cancel
        </Button>
      </div>
    </LandingPageLayout>
  );
};

export default Consent;
